import type { AccountResponse, GroupResponse } from "@ccflare/api";
import { AccountListItem } from "./AccountListItem";

interface AccountListProps {
	accounts: AccountResponse[] | undefined;
	onPauseToggle: (account: AccountResponse) => void;
	onRemove: (account: AccountResponse) => void;
	onRename: (account: AccountResponse) => void;
	onReauth?: (account: AccountResponse) => void;
	groups?: GroupResponse[];
	onMoveGroup?: (account: AccountResponse, groupId: string) => void;
}

const UNGROUPED_KEY = "__ungrouped__";

export function AccountList({
	accounts,
	onPauseToggle,
	onRemove,
	onRename,
	onReauth,
	groups,
	onMoveGroup,
}: AccountListProps) {
	if (!accounts || accounts.length === 0) {
		return <p className="text-muted-foreground">No accounts configured</p>;
	}

	const renderItem = (account: AccountResponse) => (
		<AccountListItem
			key={account.id}
			account={account}
			// Active is now a per-(provider, group) fact computed server-side from
			// the rotation state, not the globally most-recently-used account.
			isActive={account.rotation.isActive}
			onPauseToggle={onPauseToggle}
			onRemove={onRemove}
			onRename={onRename}
			onReauth={onReauth}
			groups={groups}
			onMoveGroup={onMoveGroup}
		/>
	);

	// Without group metadata, fall back to a flat list (still per-group-correct
	// Active badges via account.rotation.isActive).
	if (!groups || groups.length === 0) {
		return <div className="space-y-2">{accounts.map(renderItem)}</div>;
	}

	const accountsByGroup = new Map<string, AccountResponse[]>();
	for (const account of accounts) {
		const key = account.groupId || UNGROUPED_KEY;
		const bucket = accountsByGroup.get(key);
		if (bucket) bucket.push(account);
		else accountsByGroup.set(key, [account]);
	}

	// Default group first, then by name; any orphaned accounts last.
	const orderedGroups = [...groups].sort((a, b) => {
		if (a.isDefault !== b.isDefault) return a.isDefault ? -1 : 1;
		return a.name.localeCompare(b.name);
	});

	const sections = orderedGroups
		.map((group) => ({ group, accounts: accountsByGroup.get(group.id) ?? [] }))
		.filter((section) => section.accounts.length > 0);

	const orphaned = accountsByGroup.get(UNGROUPED_KEY) ?? [];

	return (
		<div className="space-y-6">
			{sections.map(({ group, accounts: groupAccounts }) => {
				const active = groupAccounts.find((a) => a.rotation.isActive);
				const needReauth = groupAccounts.filter((a) => a.authError).length;
				return (
					<div key={group.id} className="space-y-2">
						<div className="flex items-baseline justify-between gap-2 px-1">
							<h3 className="text-sm font-semibold">
								{group.name}
								{group.isDefault && (
									<span className="text-muted-foreground"> (default)</span>
								)}
							</h3>
							<span className="text-xs text-muted-foreground">
								{groupAccounts.length}{" "}
								{groupAccounts.length === 1 ? "account" : "accounts"}
								{active ? ` · active: ${active.name}` : " · none active"}
								{needReauth > 0 && (
									<span className="text-destructive">
										{` · ${needReauth} need${needReauth === 1 ? "s" : ""} re-login`}
									</span>
								)}
							</span>
						</div>
						<div className="space-y-2">{groupAccounts.map(renderItem)}</div>
					</div>
				);
			})}
			{orphaned.length > 0 && (
				<div className="space-y-2">
					<div className="px-1">
						<h3 className="text-sm font-semibold text-muted-foreground">
							Ungrouped
						</h3>
					</div>
					<div className="space-y-2">{orphaned.map(renderItem)}</div>
				</div>
			)}
		</div>
	);
}
